import { useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Mail01, File02, Package, AlertCircle, CheckCircle, AlertTriangle, Trash01, Edit05, ChevronRight, ChevronDown, Users01, Eye, ArrowRight } from "@untitledui/icons";
import type { ActivityItem } from "@/types/activity";
import type { NavigationState } from "@/types/navigation";
import { participantToFirstName } from "@/lib/format-participant-display";
import { formatEmailListDate } from "@/lib/format-email-list-date";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { AttachmentInlineRow } from "@/components/application/activity-feed/attachment-inline-row";
import { useIssueTypeLabel } from "@/hooks/api/use-issue-types";
import { getCategoryLabel } from "@/utils/activity-labels";

const IconForType = ({ type, activityCategory }: { type: ActivityItem["type"]; activityCategory?: string }) => {
    // Contact / business partner activities
    if (activityCategory === "contact" || type === "contact_added") {
        return <Users01 className="size-5 text-brand-secondary" />;
    }
    // Issue activities
    if (activityCategory === "issue") {
        return <AlertCircle className="size-5 text-warning-600" />;
    }
    switch (type) {
        case "email_new_shipment_request":
            return <Mail01 className="size-5 text-brand-secondary" />;
        case "email_attention_required":
            return <AlertTriangle className="size-5 text-warning-500" />;
        case "document_updated":
            return <File02 className="size-5 text-brand-secondary" />;
        case "status_update":
        case "expedition_status_changed":
            return <Package className="size-5 text-brand-secondary" />;
        case "expedition_modified":
            return <Edit05 className="size-5 text-brand-secondary" />;
        case "customs_issue":
            return <AlertCircle className="size-5 text-error-600" />;
        case "seal_change":
            return <CheckCircle className="size-5 text-success-600" />;
        case "resolution_invite":
            return <AlertCircle className="size-5 text-warning-600" />;
        default:
            return <Package className="size-5 text-tertiary" />;
    }
};

interface ActivityItemViewProps {
    item: ActivityItem;
    /** Callback when item is clicked - use for marking as read */
    onRead?: (itemId: string) => void;
    /** Callback when an email modal action is clicked */
    onOpenEmailModal?: (emailId: string) => void;
    /** Hide direct "open email" action button when row itself is clickable (e.g. trade-comms). */
    hideOpenEmailAction?: boolean;
    /**
     * Trade Comms inbox (non-limbo): compact row — no generated title line, no attention badges,
     * neutral mail icon, subject first then participants.
     */
    tradeCommsInboxEmailList?: boolean;
    /** Optional state to pass when navigating (for context-aware back). Set from parent via navigationStateFrom(location). */
    stateFrom?: NavigationState;
}

export const ActivityItemView = ({
    item,
    onRead,
    onOpenEmailModal,
    hideOpenEmailAction = false,
    tradeCommsInboxEmailList = false,
    stateFrom,
}: ActivityItemViewProps) => {
    const { t } = useTranslation(["pages:activity"]);
    const getIssueTypeLabel = useIssueTypeLabel();
    const [recapOpen, setRecapOpen] = useState(false);
    const [participantsOpen, setParticipantsOpen] = useState(false);
    // Use email received date when available (inbox list), so today/yesterday/other-day rules apply correctly
    const dateForLabel =
        item.metadata?.emailReceivedAt != null ? new Date(item.metadata.emailReceivedAt) : item.timestamp;
    const dateLabel = formatEmailListDate(dateForLabel, t("tradeComms.yesterday", { defaultValue: "Yesterday" }));

    const handleClick = () => {
        // Mark as read when clicking on an unread item
        if (!item.isRead && onRead) {
            onRead(item.id);
        }
    };

    // Check if this is an email-type activity
    const isEmailActivity = item.type === "email_new_shipment_request" || item.type === "email_attention_required";
    const inboxEmailListLayout = tradeCommsInboxEmailList && isEmailActivity;
    const hasEmailInfo = item.metadata?.emailFrom || item.metadata?.emailSubject;
    const hasEmailSummary = item.metadata?.emailSummary;
    const hasContainerNumber = item.metadata?.containerNumber;

    // Check if this is a document-type activity (by category or type)
    const isDocumentActivity = item.activityCategory === "document" || item.type === "document_updated";
    const hasDocumentInfo = item.metadata?.documentName || item.metadata?.documentCategory || item.metadata?.documentType;

    // Check if this is an issue-type activity
    const isIssueActivity = item.activityCategory === "issue" || item.metadata?.issueType;
    const hasIssueInfo = item.metadata?.reportedBy || item.metadata?.issueType || item.metadata?.issueSeverity;

    // Check if this is an expedition modification activity
    const isExpeditionModifiedActivity = item.type === "expedition_modified";
    const hasExpeditionModifiedInfo = item.metadata?.updatedBy || item.metadata?.updatedFields;
    const hasFieldChanges = item.metadata?.fieldChanges && Object.keys(item.metadata.fieldChanges).length > 0;

    // Container creation card
    const isContainerCreated = item.activityCategory === "tracking" && item.metadata?.creationSource;
    const hasContainerCreationInfo = item.metadata?.routeFrom || item.metadata?.shippingLine || item.metadata?.creationSource;

    // Phase transition with doc checklist
    const hasDocChecklist = Array.isArray(item.metadata?.requiredDocsStatus) && item.metadata.requiredDocsStatus.length > 0;

    // Check for status change info
    const hasStatusChange = item.metadata?.oldStatus && item.metadata?.newStatus;

    // Check for delay info
    const hasDelayInfo = item.metadata?.delayHours != null || item.metadata?.originalEta;

    // Check for cross-check info
    const hasCrossCheckInfo = item.metadata?.crossCheckPassed != null || item.metadata?.validationFailures;

    // Check for risk alert info
    const hasRiskInfo = item.metadata?.overallRisk != null;

    // Check for phase transition info
    const hasPhaseInfo = item.metadata?.oldPhase && item.metadata?.newPhase;

    // Collapsible state for validation failures
    const [failuresOpen, setFailuresOpen] = useState(false);

    const formatFieldName = (field: string): string => {
        const fieldLabels: Record<string, string> = {
            actual_departure_date: t("activityItem.fields.actualDepartureDate"),
            risk_score: t("activityItem.fields.riskScore"),
            destination_port_id: t("activityItem.fields.destinationPortId"),
            total_value_usd: t("activityItem.fields.totalValueUsd"),
            physical_status: t("activityItem.fields.physicalStatus"),
            status: t("activityItem.fields.status"),
            container_number: t("activityItem.fields.containerNumber"),
            booking_number: t("activityItem.fields.bookingNumber"),
            estimated_arrival_date: t("activityItem.fields.estimatedArrivalDate"),
            estimated_departure_date: t("activityItem.fields.estimatedDepartureDate"),
        };
        return fieldLabels[field] || field.replace(/_/g, " ");
    };

    const hasParticipantsMeta =
        !!(item.metadata?.accountEmail || item.metadata?.emailFrom || item.metadata?.emailReceivedAt);

    const emailParticipantsRow = hasParticipantsMeta ? (
        <div className={inboxEmailListLayout ? (item.metadata?.emailSubject ? "mt-1.5" : "mt-0") : "mt-1.5"}>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setParticipantsOpen((prev) => !prev);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        setParticipantsOpen((prev) => !prev);
                    }
                }}
                className="flex w-full min-w-0 items-center gap-2 text-left text-sm text-secondary"
                role="button"
                tabIndex={0}
            >
                <Users01 className="size-4 shrink-0 text-tertiary" />
                <span className="min-w-0 flex-1 truncate">
                    {[...new Set([item.metadata?.accountEmail, item.metadata?.emailFrom].filter((x): x is string => Boolean(x)))].map(participantToFirstName).join(", ") || "—"}
                </span>
                {(item.metadata?.emailReceivedAt || item.timestamp) && (
                    <span className="shrink-0 text-tertiary">{dateLabel}</span>
                )}
                {participantsOpen ? (
                    <ChevronDown className="size-4 shrink-0 text-tertiary" />
                ) : (
                    <ChevronRight className="size-4 shrink-0 text-tertiary" />
                )}
            </button>
            {participantsOpen && (
                <div className="mt-1 space-y-0.5 pl-6 text-sm">
                    {item.metadata?.accountEmail && (
                        <p className="text-secondary">
                            <span className="text-tertiary">{t("activityItem.account")}</span>{" "}
                            <span className="font-medium">{item.metadata.accountEmail}</span>
                        </p>
                    )}
                    {item.metadata?.emailFrom && (
                        <p className="text-secondary">
                            <span className="text-tertiary">{t("activityItem.from")}</span> {item.metadata.emailFrom}
                        </p>
                    )}
                    {item.metadata?.emailReceivedAt && (
                        <p className="text-secondary">
                            <span className="text-tertiary">{t("activityItem.received")}</span>{" "}
                            {new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(item.metadata.emailReceivedAt))}
                        </p>
                    )}
                </div>
            )}
        </div>
    ) : null;

    return (
        <div
            className="flex items-start gap-3 cursor-pointer p-3 transition-colors bg-transparent hover:bg-secondary/20 dark:hover:bg-secondary/30"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={inboxEmailListLayout ? (item.metadata?.emailSubject || item.title) : item.title}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    handleClick();
                }
            }}
        >
            <div className="mt-1">
                {inboxEmailListLayout ? (
                    <Mail01 className="size-5 text-tertiary" aria-hidden />
                ) : (
                    <IconForType type={item.type} activityCategory={item.activityCategory} />
                )}
            </div>
            <div className="flex-1 min-w-0">
                {!inboxEmailListLayout && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4 className={`text-sm text-primary ${!item.isRead ? "font-semibold" : "font-medium"}`}>{item.title}</h4>
                        {!item.isRead && <span className="inline-flex h-2 w-2 rounded-full bg-brand-solid shrink-0" />}
                        {item.attentionLevel === 'action_needed' && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-warning-700 bg-warning-50 px-2 py-0.5 rounded-full shrink-0">
                                <AlertCircle className="size-3" />
                                {t("activityItem.actionNeeded")}
                            </span>
                        )}
                        {item.attentionLevel === 'require_review' && item.isRead && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700 bg-success-50 px-2 py-0.5 rounded-full shrink-0">
                                <CheckCircle className="size-3" />
                                {t("activityItem.reviewed")}
                            </span>
                        )}
                        {item.attentionLevel === 'require_review' && !item.isRead && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 bg-brand-50 px-2 py-0.5 rounded-full shrink-0">
                                <Eye className="size-3" />
                                {t("activityItem.requiresReview")}
                            </span>
                        )}
                        {item.activityCategory && !isEmailActivity && (
                            <span className="text-xs font-medium text-tertiary bg-secondary px-1.5 py-0.5 rounded shrink-0">
                                {getCategoryLabel(item.activityCategory)}
                            </span>
                        )}
                    </div>
                )}

                {/* Email: Trade Comms inbox — subject first, then participants; otherwise legacy order */}
                {isEmailActivity && hasEmailInfo && (
                    <>
                        {inboxEmailListLayout ? (
                            <>
                                {item.metadata?.emailSubject && (
                                    <p className="mt-0 flex items-start gap-2 text-sm font-semibold text-primary">
                                        {!item.isRead && (
                                            <span className="mt-1.5 inline-flex h-2 w-2 shrink-0 rounded-full bg-brand-solid" aria-hidden />
                                        )}
                                        <span className="min-w-0 flex-1">{item.metadata.emailSubject}</span>
                                    </p>
                                )}
                                {emailParticipantsRow}
                            </>
                        ) : (
                            <>
                                {emailParticipantsRow}
                                {item.metadata?.emailSubject && (
                                    <p className="mt-1 text-sm font-semibold text-primary">{item.metadata.emailSubject}</p>
                                )}
                            </>
                        )}
                    </>
                )}

                {/* Email summary - collapsible, closed by default */}
                {isEmailActivity && hasEmailSummary && (
                    <div className="mt-1.5">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setRecapOpen((prev) => !prev);
                            }}
                            className="flex items-center gap-1 text-sm text-tertiary hover:text-secondary"
                        >
                            {recapOpen ? (
                                <ChevronDown className="size-4 shrink-0" />
                            ) : (
                                <ChevronRight className="size-4 shrink-0" />
                            )}
                            <span>{recapOpen ? t("activityItem.hideRecap") : t("activityItem.showRecap")}</span>
                        </button>
                        {recapOpen && (
                            <p className="mt-1 text-sm text-secondary italic">
                                {item.metadata?.emailSummary}
                            </p>
                        )}
                    </div>
                )}

                {/* Document info section - shown for document activities */}
                {isDocumentActivity && hasDocumentInfo && (
                    <div className="mt-1.5 space-y-0.5 text-sm">
                        {item.metadata?.documentName && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.file")}</span>{" "}
                                <span className="font-medium">{item.metadata.documentName}</span>
                            </p>
                        )}
                        {/* Document type - the classification result */}
                        {item.metadata?.documentType && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.type")}</span>{" "}
                                <span className="font-medium">{item.metadata.documentType}</span>
                            </p>
                        )}
                        {item.metadata?.documentCategory && !item.metadata?.documentType && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.category")}</span>{" "}
                                <span className="capitalize">{item.metadata.documentCategory.replace(/_/g, " ")}</span>
                            </p>
                        )}
                        {/* Who performed the action - use uploadedBy if available (for version uploads), else actor */}
                        {(item.metadata?.uploadedBy || item.actor?.name) && (
                            <p className="text-secondary">
                                <span className="text-tertiary">
                                    {item.metadata?.uploadedBy ? t("activityItem.uploadedBy") : t("activityItem.classifiedBy")}
                                </span>{" "}
                                {item.metadata?.uploadedBy || item.actor?.name}
                            </p>
                        )}
                        {item.metadata?.uploadSource && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.source")}</span>{" "}
                                {item.metadata.uploadSource === "email" ? t("activityItem.sourceEmail") :
                                 item.metadata.uploadSource === "manual" ? t("activityItem.sourceManual") :
                                 item.metadata.uploadSource === "api" ? "API" : item.metadata.uploadSource}
                            </p>
                        )}
                    </div>
                )}

                {/* Issue info section - shown for issue activities */}
                {isIssueActivity && hasIssueInfo && (
                    <div className="mt-1.5 space-y-0.5 text-sm">
                        {item.metadata?.reportedBy && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.reportedBy")}</span>{" "}
                                <span className="font-medium">{item.metadata.reportedBy}</span>
                            </p>
                        )}
                        {item.metadata?.issueType && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.type")}</span>{" "}
                                <span className="font-medium">{getIssueTypeLabel(item.metadata.issueType)}</span>
                            </p>
                        )}
                        {item.metadata?.issueSeverity && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.severity")}</span>{" "}
                                <span className={`capitalize font-medium ${
                                    item.metadata.issueSeverity === "critical" ? "text-error-600" :
                                    item.metadata.issueSeverity === "high" ? "text-warning-600" :
                                    "text-secondary"
                                }`}>{item.metadata.issueSeverity}</span>
                            </p>
                        )}
                    </div>
                )}

                {/* Contact info section - shown for contact activities */}
                {item.activityCategory === "contact" && (item.metadata?.contactName || item.metadata?.contactEmail) && (
                    <div className="mt-1.5 space-y-0.5 text-sm">
                        {item.metadata?.contactName && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.name")}</span>{" "}
                                <span className="font-medium">{item.metadata.contactName}</span>
                            </p>
                        )}
                        {item.metadata?.contactEmail && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.email")}</span>{" "}
                                {item.metadata.contactEmail}
                            </p>
                        )}
                        {item.metadata?.contactCompanyName && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.company")}</span>{" "}
                                <span className="font-medium">{item.metadata.contactCompanyName}</span>
                            </p>
                        )}
                        {item.metadata?.contactRelationshipType && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.role")}</span>{" "}
                                <span className="capitalize">{item.metadata.contactRelationshipType.replace(/_/g, " ")}</span>
                            </p>
                        )}
                    </div>
                )}

                {/* Expedition modified info section — with before/after */}
                {isExpeditionModifiedActivity && hasExpeditionModifiedInfo && (
                    <div className="mt-1.5 space-y-0.5 text-sm">
                        {item.metadata?.updatedBy && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.modifiedBy")}</span>{" "}
                                <span className="font-medium">{item.metadata.updatedBy}</span>
                            </p>
                        )}
                        {hasFieldChanges ? (
                            <div className="space-y-0.5">
                                {Object.entries(item.metadata!.fieldChanges!).map(([field, vals]) => (
                                    <p key={field} className="text-secondary">
                                        <span className="text-tertiary">{formatFieldName(field)}:</span>{" "}
                                        <span className="line-through text-tertiary">{vals.old || "—"}</span>
                                        {" → "}
                                        <span className="font-medium">{vals.new || "—"}</span>
                                    </p>
                                ))}
                            </div>
                        ) : item.metadata?.updatedFields && item.metadata.updatedFields.length > 0 ? (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.modifiedFields")}</span>{" "}
                                <span>{item.metadata.updatedFields.map(formatFieldName).join(", ")}</span>
                            </p>
                        ) : null}
                    </div>
                )}

                {/* Container creation context */}
                {isContainerCreated && hasContainerCreationInfo && (
                    <div className="mt-1.5 space-y-0.5 text-sm">
                        {item.metadata?.routeFrom && item.metadata?.routeTo && (
                            <p className="text-secondary">
                                <span className="text-tertiary">Rotta:</span>{" "}
                                <span className="font-medium">{item.metadata.routeFrom} → {item.metadata.routeTo}</span>
                            </p>
                        )}
                        {item.metadata?.shippingLine && (
                            <p className="text-secondary">
                                <span className="text-tertiary">Shipping line:</span>{" "}
                                <span className="font-medium">{item.metadata.shippingLine}</span>
                            </p>
                        )}
                        {item.metadata?.bookingNumber && (
                            <p className="text-secondary">
                                <span className="text-tertiary">Booking:</span>{" "}
                                <span className="font-medium">{item.metadata.bookingNumber}</span>
                            </p>
                        )}
                        {item.metadata?.creationSource && (
                            <p className="text-secondary">
                                <span className="text-tertiary">Fonte:</span>{" "}
                                <span className="capitalize">{item.metadata.creationSource}</span>
                            </p>
                        )}
                    </div>
                )}

                {/* Status change section */}
                {hasStatusChange && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-sm">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-secondary text-secondary">
                            {item.metadata!.oldStatus!.replace(/_/g, " ")}
                        </span>
                        <ArrowRight className="size-3.5 text-tertiary" />
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-brand-50 text-brand-700">
                            {item.metadata!.newStatus!.replace(/_/g, " ")}
                        </span>
                    </div>
                )}

                {/* Delay alert section */}
                {hasDelayInfo && (
                    <div className="mt-1.5 space-y-0.5 text-sm">
                        {item.metadata?.delayHours != null && (
                            <p className={`font-medium ${
                                (item.metadata.delayHours) >= 48 ? "text-error-600" :
                                (item.metadata.delayHours) >= 24 ? "text-warning-600" :
                                "text-secondary"
                            }`}>
                                {t("activityItem.delayHours", { hours: item.metadata.delayHours })}
                            </p>
                        )}
                        {item.metadata?.originalEta && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.originalEta")}</span>{" "}
                                {new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "2-digit", year: "2-digit" }).format(new Date(item.metadata.originalEta))}
                            </p>
                        )}
                        {item.metadata?.currentEta && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.currentEta")}</span>{" "}
                                <span className="font-medium">{new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "2-digit", year: "2-digit" }).format(new Date(item.metadata.currentEta))}</span>
                            </p>
                        )}
                        {item.metadata?.vesselName && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.vessel")}</span>{" "}
                                {item.metadata.vesselName}
                            </p>
                        )}
                    </div>
                )}

                {/* Cross-check results section */}
                {hasCrossCheckInfo && (
                    <div className="mt-1.5 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                            {item.metadata?.crossCheckPassed != null && (
                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-success-50 text-success-700">
                                    <CheckCircle className="size-3" />
                                    {t("activityItem.passed", { count: item.metadata.crossCheckPassed })}
                                </span>
                            )}
                            {(item.metadata?.crossCheckFailed ?? 0) > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-error-50 text-error-700">
                                    <AlertCircle className="size-3" />
                                    {t("activityItem.failed", { count: item.metadata!.crossCheckFailed })}
                                </span>
                            )}
                            {(item.metadata?.missingDocuments ?? 0) > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-warning-50 text-warning-700">
                                    {t("activityItem.missing", { count: item.metadata!.missingDocuments })}
                                </span>
                            )}
                            {(item.metadata?.crossCheckSkipped ?? 0) > 0 && (
                                <span className="text-xs text-tertiary">
                                    {t("activityItem.skipped", { count: item.metadata!.crossCheckSkipped })}
                                </span>
                            )}
                        </div>
                        {item.metadata?.validationFailures && item.metadata.validationFailures.length > 0 && (
                            <div className="mt-1">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFailuresOpen((prev) => !prev);
                                    }}
                                    className="flex items-center gap-1 text-xs text-tertiary hover:text-secondary"
                                >
                                    {failuresOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                                    <span>{failuresOpen ? t("activityItem.hideDetails") : t("activityItem.issuesDetected", { count: item.metadata.validationFailures.length })}</span>
                                </button>
                                {failuresOpen && (
                                    <ul className="mt-1 space-y-1 pl-4">
                                        {item.metadata.validationFailures.slice(0, 3).map((f, i) => (
                                            <li key={i} className="text-xs text-secondary">
                                                <span className={`inline-flex rounded px-1 py-0.5 text-[10px] font-medium mr-1 ${
                                                    f.severity === "critical" ? "bg-error-50 text-error-700" :
                                                    f.severity === "high" ? "bg-warning-50 text-warning-700" :
                                                    "bg-secondary text-tertiary"
                                                }`}>{f.severity}</span>
                                                <span className="font-medium">{f.rule_name.replace(/_/g, " ")}</span>
                                                {f.message && <span className="text-tertiary"> — {f.message}</span>}
                                            </li>
                                        ))}
                                        {item.metadata.validationFailures.length > 3 && (
                                            <li className="text-xs text-tertiary">{t("activityItem.moreOthers", { count: item.metadata.validationFailures.length - 3 })}</li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Risk alert section */}
                {hasRiskInfo && (
                    <div className="mt-1.5 space-y-0.5 text-sm">
                        <p className="text-secondary">
                            <span className="text-tertiary">{t("activityItem.risk")}</span>{" "}
                            <span className={`font-bold ${
                                item.metadata!.overallRisk! > 0.75 ? "text-error-600" :
                                item.metadata!.overallRisk! > 0.50 ? "text-warning-600" :
                                item.metadata!.overallRisk! > 0.25 ? "text-warning-500" :
                                "text-success-600"
                            }`}>
                                {Math.round(item.metadata!.overallRisk! * 100)}%
                            </span>
                        </p>
                        {item.metadata?.riskFactors && (
                            <div className="flex flex-wrap gap-1.5">
                                {Object.entries(item.metadata.riskFactors)
                                    .sort(([, a], [, b]) => b - a)
                                    .slice(0, 3)
                                    .map(([factor, score]) => (
                                        <span key={factor} className="inline-flex items-center gap-1 text-xs text-secondary">
                                            <span className={`inline-block size-1.5 rounded-full ${
                                                score > 0.75 ? "bg-error-500" :
                                                score > 0.50 ? "bg-warning-500" :
                                                score > 0.25 ? "bg-warning-400" :
                                                "bg-success-500"
                                            }`} />
                                            {factor.replace(/_/g, " ")}
                                        </span>
                                    ))}
                            </div>
                        )}
                        {item.metadata?.activeIssuesCount != null && item.metadata.activeIssuesCount > 0 && (
                            <p className="text-secondary">
                                <span className="text-tertiary">{t("activityItem.activeIssues")}</span> {item.metadata.activeIssuesCount}
                            </p>
                        )}
                    </div>
                )}

                {/* Phase transition section — with doc checklist */}
                {hasPhaseInfo && (
                    <div className="mt-1.5 space-y-1 text-sm">
                        <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-secondary text-secondary capitalize">
                                {item.metadata!.oldPhase!.replace(/_/g, " ")}
                            </span>
                            <ArrowRight className="size-3.5 text-tertiary" />
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-brand-50 text-brand-700 capitalize">
                                {item.metadata!.newPhase!.replace(/_/g, " ")}
                            </span>
                        </div>
                        {item.metadata?.triggerSource && (
                            <p className="text-tertiary">
                                Sorgente: <span className="text-secondary capitalize">{item.metadata.triggerSource}</span>
                            </p>
                        )}
                        {item.metadata?.issuesClosed != null && item.metadata.issuesClosed > 0 && (
                            <p className="text-secondary">
                                <span className="text-success-600 font-medium">{t("activityItem.issuesAutoClosed", { count: item.metadata.issuesClosed })}</span>
                            </p>
                        )}
                        {hasDocChecklist && (
                            <div className="mt-1 space-y-0.5">
                                <p className="text-tertiary font-medium">Documenti richiesti:</p>
                                {item.metadata!.requiredDocsStatus!.map((doc) => (
                                    <p key={doc.document_type} className="flex items-center gap-1.5 text-secondary">
                                        <span className={doc.present ? "text-success-600" : "text-error-500"}>
                                            {doc.present ? "\u2713" : "\u2717"}
                                        </span>
                                        <span className={doc.present ? "" : "font-medium"}>
                                            {doc.document_type.replace(/_/g, " ")}
                                        </span>
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Container number - displayed prominently in bold */}
                {hasContainerNumber && (
                    <p className="mt-1.5 text-sm">
                        <span className="text-tertiary">{t("activityItem.container")}</span>{" "}
                        <span className="font-bold text-primary">{item.metadata?.containerNumber}</span>
                    </p>
                )}

                {item.description && !(isEmailActivity && (item.metadata?.emailAttentionLabels?.length ?? 0) > 0) && (
                    <p className="mt-1.5 text-sm text-tertiary">{item.description}</p>
                )}
                {item.attachments && item.attachments.length > 0 && <AttachmentInlineRow attachments={item.attachments} />}
                {/* Entity deleted indicator */}
                {item.entityDeleted && (
                    <div className="mt-2 flex items-center gap-1.5 text-sm text-tertiary">
                        <Trash01 className="size-4" />
                        <span>{t("activityItem.containerDeleted")}</span>
                    </div>
                )}

                {item.actions && item.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {item.actions
                            .filter((a) => !(hideOpenEmailAction && a.modalType === "email"))
                            .map((a, idx) => {
                            // Severity-based button colors for issue actions
                            const severityClasses: Record<string, string> = {
                                critical: "bg-error-solid hover:opacity-90",
                                high: "bg-warning-solid hover:opacity-90",
                                medium: "bg-brand-solid hover:bg-brand-solid_hover",
                                low: "bg-fg-quaternary hover:opacity-90",
                            };
                            const defaultClasses = "bg-brand-solid hover:bg-brand-solid_hover";
                            const btnColorClass = a.severity ? (severityClasses[a.severity] ?? defaultClasses) : defaultClasses;
                            const btnBase = `inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset ${btnColorClass}`;

                            // If the entity is deleted, show a disabled button with tooltip
                            if (item.entityDeleted) {
                                return (
                                    <Tooltip key={idx} title={t("activityItem.containerDeletedTooltip")}>
                                        <span
                                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-disabled px-3 py-1.5 text-sm font-semibold text-disabled cursor-not-allowed"
                                        >
                                            {a.label}
                                        </span>
                                    </Tooltip>
                                );
                            }
                            // If the action has a modalType, render a button instead of a link
                            if (a.modalType === "email" && a.modalId && onOpenEmailModal) {
                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenEmailModal(a.modalId!);
                                        }}
                                        className={btnBase}
                                    >
                                        {a.label}
                                    </button>
                                );
                            }
                            // Default: render as a Link for navigation
                            return (
                                <Link key={idx} to={a.href} state={stateFrom} className={btnBase}>
                                    {a.label}
                                </Link>
                            );
                            })}
                    </div>
                )}
                <div className="mt-2 flex items-center gap-2 text-xs text-tertiary">
                    {isEmailActivity && item.metadata?.emailSyncedAt ? (
                        <span>
                            {t("activityItem.synced")}{" "}
                            {new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }).format(
                                new Date(item.metadata.emailSyncedAt)
                            )}
                        </span>
                    ) : (
                        <span>{dateLabel}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivityItemView;


